import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiError, apiFetch } from '@/lib/api/client';
import { useToast } from '@/components/ui/toast';
import { DEALS_QUERY_KEY, patchDealInCaches, restoreDealItem, snapshotDealItem } from './useDeals';
import { type ApiDealResponse, readApiETag } from './types';

interface ResumeArgs {
  id: string;
  /** ETag (RowVersion base64) do item, para o If-Match. */
  etag: string;
}

/**
 * Retoma um deal em pausa via POST /api/deals/{id}/resume com If-Match (ETag). Sem comentário
 * (item 4). Otimista: marca auxiliaryStatus 'Active' no cache por prefixo; 412 → rollback + refetch
 * + aviso. Invalida no sucesso porque a transição muda a "coluna" de status (espelha Reactivate).
 */
export function useResumeDeal() {
  const qc = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, etag }: ResumeArgs) =>
      apiFetch<ApiDealResponse>(`/api/deals/${id}/resume`, {
        method: 'POST',
        headers: { 'If-Match': `"${etag}"` },
      }),
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: DEALS_QUERY_KEY });
      const prev = snapshotDealItem(qc, id);
      patchDealInCaches(qc, id, (it) => ({ ...it, auxiliaryStatus: 'Active' }));
      return { prev };
    },
    onError: (err, vars, ctx) => {
      if (ctx?.prev) restoreDealItem(qc, vars.id, ctx.prev);
      if ((err as ApiError).status === 412) {
        toast('Esse deal foi alterado por outra pessoa. Atualizei a lista.', 'warn');
        void qc.invalidateQueries({ queryKey: DEALS_QUERY_KEY });
      } else {
        toast('Não foi possível retomar o deal. Tente de novo.', 'danger');
      }
    },
    onSuccess: (resp) => {
      patchDealInCaches(qc, resp.id, (it) => ({
        ...it,
        auxiliaryStatus: resp.auxiliaryStatus,
        eTag: readApiETag(resp) || it.eTag,
      }));
      void qc.invalidateQueries({ queryKey: DEALS_QUERY_KEY });
      toast('Deal retomado.');
    },
  });
}
